<template>
  <div class="dash-root">
    <vue-element-loading :active="appLoading" spinner="bar-fade-scale" color="#3ea56e" size="128" is-full-screen />
    <v-layout v-if="ServerError" wrap justify-center>
      <v-flex px-5 pt-5 xs12 sm12 md12 lg12 xl12 text-center>
        <ServerError />
      </v-flex>
    </v-layout>
    <div v-else>
      <div class="dash-page-header">
        <div>
          <p class="dash-page-header__eyebrow">Reports</p>
          <h1 class="dash-page-header__title">Sales Report</h1>
        </div>
        <div class="dash-page-header__actions">
          <v-btn class="action-btn-export action-btn-excel" @click="exportExcel" depressed>
            <v-icon left size="20">mdi-file-excel</v-icon> Download Excel
          </v-btn>
          <v-btn class="action-btn-export action-btn-pdf ml-2" @click="exportPdf" depressed>
            <v-icon left size="20">mdi-file-pdf-box</v-icon> Download PDF
          </v-btn>
        </div>
      </div>

      <!-- Filters -->
      <div class="filters-card">
        <v-row align="center">
          <v-col cols="12" sm="6" md="2">
            <v-menu v-model="fromDateMenu" :close-on-content-click="false" transition="scale-transition" offset-y min-width="auto">
              <template v-slot:activator="{ on, attrs }">
                <v-text-field v-model="filters.fromDate" label="From Date" prepend-inner-icon="mdi-calendar" readonly dense outlined hide-details class="modern-input" v-bind="attrs" v-on="on"></v-text-field>
              </template>
              <v-date-picker v-model="filters.fromDate" @input="fromDateMenu = false" no-title scrollable color="#3ea56e"></v-date-picker>
            </v-menu>
          </v-col>
          <v-col cols="12" sm="6" md="2">
            <v-menu v-model="toDateMenu" :close-on-content-click="false" transition="scale-transition" offset-y min-width="auto">
              <template v-slot:activator="{ on, attrs }">
                <v-text-field v-model="filters.toDate" label="To Date" prepend-inner-icon="mdi-calendar" readonly dense outlined hide-details class="modern-input" v-bind="attrs" v-on="on"></v-text-field>
              </template>
              <v-date-picker v-model="filters.toDate" @input="toDateMenu = false" no-title scrollable color="#3ea56e"></v-date-picker>
            </v-menu>
          </v-col>
          <v-col cols="12" sm="6" md="3">
            <v-autocomplete v-model="filters.sellerId" :items="sellers" item-text="name" item-value="_id" label="Seller" dense outlined hide-details clearable class="modern-input"></v-autocomplete>
          </v-col>
          <v-col cols="12" sm="6" md="2">
            <v-select v-model="filters.orderStatus" :items="statusOptions" label="Order Status" dense outlined hide-details class="modern-input"></v-select>
          </v-col>
          <v-col cols="12" sm="6" md="3" class="d-flex gap-2">
            <v-btn class="btn-filter flex-grow-1" @click="getReport" depressed>Filter</v-btn>
            <v-btn class="btn-reset flex-grow-1" @click="resetFilters" depressed outlined>Reset</v-btn>
          </v-col>
        </v-row>
      </div>

      <!-- Summary Cards -->
      <div class="summary-grid mb-6" v-if="summary">
        <v-card class="summary-card summary-card-blue" elevation="0">
          <div class="summary-icon"><v-icon color="#0284c7" size="28">mdi-currency-inr</v-icon></div>
          <div class="summary-info">
            <div class="summary-title">Total Sales</div>
            <div class="summary-value">₹{{ summary.totalSales }}</div>
          </div>
        </v-card>
        <v-card class="summary-card summary-card-green" elevation="0">
          <div class="summary-icon"><v-icon color="#16a34a" size="28">mdi-package-variant</v-icon></div>
          <div class="summary-info">
            <div class="summary-title">Total Items</div>
            <div class="summary-value">{{ summary.totalItems }}</div>
          </div>
        </v-card>
        <v-card class="summary-card summary-card-orange" elevation="0">
          <div class="summary-icon"><v-icon color="#ea580c" size="28">mdi-receipt-text-outline</v-icon></div>
          <div class="summary-info">
            <div class="summary-title">Total Tax</div>
            <div class="summary-value">₹{{ summary.totalTax.toFixed(2) }}</div>
          </div>
        </v-card>
        <v-card class="summary-card summary-card-purple" elevation="0">
          <div class="summary-icon"><v-icon color="#9333ea" size="28">mdi-box-cutter</v-icon></div>
          <div class="summary-info">
            <div class="summary-title">Total Packing</div>
            <div class="summary-value">₹{{ summary.totalPacking }}</div>
          </div>
        </v-card>
        <v-card class="summary-card summary-card-teal" elevation="0">
          <div class="summary-icon"><v-icon color="#0d9488" size="28">mdi-account-group-outline</v-icon></div>
          <div class="summary-info">
            <div class="summary-title">Unique Orders</div>
            <div class="summary-value">{{ summary.uniqueOrders }}</div>
          </div>
        </v-card>
      </div>

      <!-- Report Table -->
      <v-card class="modern-card mt-6" elevation="0">
        <v-data-table :headers="headers" :items="reportData" :items-per-page="20" class="modern-table" :search="search">
          <template v-slot:top>
            <div class="table-header">
              <v-text-field v-model="search" placeholder="Search sales..." prepend-inner-icon="mdi-magnify" outlined dense hide-details class="modern-search-input"></v-text-field>
            </div>
          </template>
          <template v-slot:[`item.create_date`]="{ item }">
            <span class="font-weight-medium" style="white-space: nowrap;">{{ formatDate(item.create_date) }}</span>
          </template>
          <template v-slot:[`item.customer`]="{ item }">
            <span class="font-weight-medium">{{ item.uid ? item.uid.firstname + ' ' + (item.uid.lastname || '') : 'N/A' }}</span>
          </template>
          <template v-slot:[`item.seller`]="{ item }">
            <span class="font-weight-medium">{{ item.sellerId ? item.sellerId.firstname + ' ' + (item.sellerId.lastname || '') : 'N/A' }}</span>
          </template>
          <template v-slot:[`item.orderStatus`]="{ item }">
            <v-chip small :color="item.orderStatus === 'Delivered' ? '#dcfce7' : item.orderStatus === 'Shipped' ? '#ffedd5' : item.orderStatus === 'Out For Delivery' ? '#e0f2fe' : item.orderStatus === 'Order Confirmed' ? '#f3e8ff' : '#f1f5f9'" :text-color="item.orderStatus === 'Delivered' ? '#16a34a' : item.orderStatus === 'Shipped' ? '#ea580c' : item.orderStatus === 'Out For Delivery' ? '#0284c7' : item.orderStatus === 'Order Confirmed' ? '#9333ea' : '#475569'" class="font-weight-bold">
              {{ item.orderStatus }}
            </v-chip>
          </template>
          <template v-slot:[`item.price`]="{ item }">
            <span class="price-text">₹{{ item.price }}</span>
          </template>
          <template v-slot:[`item.TotalAmount`]="{ item }">
            <span class="price-text font-weight-bold">₹{{ item.TotalAmount }}</span>
          </template>
        </v-data-table>
      </v-card>
    </div>
  </div>
</template>

<script>
import axios from "axios";

export default {
  data() {
    return {
      appLoading: false,
      ServerError: false,
      search: "",
      fromDateMenu: false,
      toDateMenu: false,
      filters: {
        fromDate: "",
        toDate: "",
        sellerId: null,
        orderStatus: "All",
      },
      statusOptions: ["All", "Order Confirmed", "Shipped", "Out For Delivery", "Delivered"],
      sellers: [],
      reportData: [],
      summary: null,
      headers: [
        { text: "Date", value: "create_date", align: "start" },
        { text: "Order ID", value: "orderID", align: "start" },
        { text: "Customer", value: "customer", align: "start" },
        { text: "Seller", value: "seller", align: "start" },
        { text: "Product", value: "productname", align: "start" },
        { text: "Price", value: "price", align: "start" },
        { text: "Qty", value: "quantity", align: "center" },
        { text: "Total", value: "TotalAmount", align: "start" },
        { text: "Status", value: "orderStatus", align: "start" },
      ],
    };
  },
  mounted() {
    this.getSellers();
    this.getReport();
  },
  methods: {
    getSellers() {
      axios({
        url: "/seller/activeall",
        method: "GET",
        headers: {
          "x-auth-token": localStorage.getItem("token"),
        },
        params: {
          count: 1000,
          page: 1,
        },
      })
        .then((response) => {
          this.sellers = response.data.data.map((s) => ({
            _id: s._id,
            name: `${s.firstname} ${s.lastname || ""} (${s.organization || "No Org"})`,
          }));
        })
        .catch((err) => {
          console.error("Error fetching sellers:", err);
        });
    },
    getReport() {
      this.appLoading = true;
      const params = { ...this.filters };
      if (params.orderStatus === "All") delete params.orderStatus;

      axios({
        url: "/admin/report/sales",
        method: "POST",
        headers: {
          "x-auth-token": localStorage.getItem("token"),
        },
        data: params,
      })
        .then((response) => {
          this.appLoading = false;
          if (response.data.status) {
            this.reportData = response.data.reportData;
            this.summary = response.data.summary;
          }
        })
        .catch((err) => {
          this.appLoading = false;
          this.ServerError = true;
          console.error("Error fetching report:", err);
        });
    },
    resetFilters() {
      this.filters = {
        fromDate: "",
        toDate: "",
        sellerId: null,
        orderStatus: "All",
      };
      this.getReport();
    },
    exportExcel() {
      this.downloadReport("/admin/report/sales/excel", "sales_report.xlsx");
    },
    exportPdf() {
      this.downloadReport("/admin/report/sales/pdf", "sales_report.pdf");
    },
    downloadReport(url, filename) {
      this.appLoading = true;
      const params = { ...this.filters };
      if (params.orderStatus === "All") delete params.orderStatus;

      axios({
        url: url,
        method: "POST",
        headers: {
          "x-auth-token": localStorage.getItem("token"),
        },
        data: params,
        responseType: "blob",
      })
        .then((response) => {
          this.appLoading = false;
          const blob = new Blob([response.data], { type: response.headers["content-type"] });
          const link = document.createElement("a");
          link.href = window.URL.createObjectURL(blob);
          link.download = filename;
          link.click();
        })
        .catch((err) => {
          this.appLoading = false;
          alert("Error downloading report");
          console.error("Download error:", err);
        });
    },
    formatDate(dateStr) {
      if (!dateStr) return "N/A";
      const date = new Date(dateStr);
      return date.toLocaleDateString() + " " + date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true });
    },
    getStatusColor(status) {
      switch (status) {
        case "Delivered": return "green";
        case "Out For Delivery": return "blue";
        case "Shipped": return "orange";
        case "Order Confirmed": return "cyan";
        default: return "grey";
      }
    },
  },
};
</script>

<style scoped>
.dash-root { font-family: poppinsmedium, Roboto, sans-serif; padding: 12px 12px; }
.dash-page-header { display: flex; align-items: flex-end; justify-content: space-between; margin-bottom: 24px; flex-wrap: wrap; gap: 16px; }
.dash-page-header__eyebrow { font-size: 13px; color: #8a93a4; margin: 0 0 4px; font-family: poppinsmedium, sans-serif; text-transform: uppercase; letter-spacing: 0.5px; }
.dash-page-header__title { font-size: 22px; font-weight: 700; color: #1a202c; margin: 0; font-family: poppinsbold, sans-serif; line-height: 1.2; }
.dash-page-header__actions { display: flex; gap: 12px; }

.filters-card { background: #ffffff; border: 1px solid #eaedf0; border-radius: 12px; padding: 20px; margin-bottom: 24px; box-shadow: 0 2px 8px rgba(0,0,0,0.02) !important; }
.btn-filter { background: #3ea56e !important; color: #ffffff !important; border-radius: 8px; text-transform: none; font-weight: 600; box-shadow: 0 4px 12px rgba(62, 165, 110, 0.2) !important; letter-spacing: 0; height: 40px !important; }
.btn-reset { border-color: #cbd5e1 !important; color: #475569 !important; border-radius: 8px; text-transform: none; font-weight: 600; letter-spacing: 0; background: #ffffff !important; height: 40px !important; }
.action-btn-export { border-radius: 8px; text-transform: none; font-weight: 600; letter-spacing: 0; height: 40px !important; }
.action-btn-excel { background: #dcfce7 !important; color: #16a34a !important; border: 1px solid #bbf7d0 !important; box-shadow: none !important; }
.action-btn-excel:hover { background: #bbf7d0 !important; }
.action-btn-pdf { background: #fee2e2 !important; color: #dc2626 !important; border: 1px solid #fecaca !important; box-shadow: none !important; }
.action-btn-pdf:hover { background: #fecaca !important; }

.summary-card { display: flex; align-items: center; padding: 20px; border-radius: 12px; border: 1px solid transparent; height: 100%; transition: all 0.2s ease; }
.summary-card:hover { transform: translateY(-2px); }
.summary-icon { width: 48px; height: 48px; border-radius: 12px; display: flex; align-items: center; justify-content: center; margin-right: 16px; background: rgba(255,255,255,0.6); }
.summary-info { display: flex; flex-direction: column; }
.summary-title { font-size: 13px; color: #64748b; font-family: poppinsmedium, sans-serif; text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 4px; }
.summary-value { font-size: 24px; font-weight: 700; color: #0f172a; font-family: poppinsbold, sans-serif; line-height: 1.2; }

.summary-grid { display: grid; grid-template-columns: repeat(5, 1fr); gap: 16px; }
@media (max-width: 1200px) { .summary-grid { grid-template-columns: repeat(3, 1fr); } }
@media (max-width: 900px) { .summary-grid { grid-template-columns: repeat(2, 1fr); } }
@media (max-width: 600px) { .summary-grid { grid-template-columns: 1fr; } }

.summary-card-blue { background: #e0f2fe; border-color: #bae6fd; }
.summary-card-green { background: #dcfce7; border-color: #bbf7d0; }
.summary-card-orange { background: #ffedd5; border-color: #fed7aa; }
.summary-card-purple { background: #f3e8ff; border-color: #e9d5ff; }
.summary-card-teal { background: #ccfbf1; border-color: #99f6e4; }

.modern-card { background: #ffffff; border: 1px solid #eaedf0; border-radius: 12px; overflow: hidden; box-shadow: 0 4px 12px rgba(0, 0, 0, 0.02) !important; }
.table-header { padding: 16px 20px; border-bottom: 1px solid #eaedf0; background: #ffffff; display: flex; align-items: center; justify-content: space-between; }
.modern-search-input { max-width: 300px; }
.modern-search-input ::v-deep .v-input__control .v-input__slot { border-radius: 8px; min-height: 40px !important; }
.gap-2 { gap: 8px; }

::v-deep .modern-table .v-data-table-header th { font-family: poppinssemibold, sans-serif; font-size: 13px !important; color: #64748b !important; text-transform: uppercase; letter-spacing: 0.5px; background-color: #f8fafc !important; border-bottom: 1px solid #eaedf0 !important; padding: 12px 16px !important; white-space: nowrap !important; vertical-align: middle !important; }
::v-deep .modern-table .v-data-table-header th span { vertical-align: middle; display: inline-flex; align-items: center; }
::v-deep .modern-table tbody td { font-family: poppinsmedium, sans-serif; font-size: 14px; color: #1a202c; border-bottom: 1px solid #eaedf0 !important; padding: 12px 16px !important; }
::v-deep .modern-table tbody tr:hover { background-color: #f8fafc !important; }
.price-text { font-family: 'Courier New', Courier, monospace; letter-spacing: 0.5px; font-weight: 600; color: #334155; }
.text-muted { color: #94a3b8; }
</style>
